import { graphql } from 'react-relay';
import type { GraphQLTaggedNode } from 'react-relay';

import { useEditorContentFragment } from '@confluence/editor-content-unified-query/entry-points/useEditorContentFragment.ts';
import type { EditorContentFragmentResult } from '@confluence/editor-content-unified-query/entry-points/useEditorContentFragment.ts';

import type { EmojiTitleQuery$data } from './__generated__/EmojiTitleQuery.graphql.ts';
import type { useEmojiTitleFragment_content$key } from './__generated__/useEmojiTitleFragment_content.graphql.ts';

// The editor's EmojiTitleQuery selection (with $skipPublishedFragment false) for
// EditorContentUnifiedQuery; same fields and arguments, so both share the store records.
const emojiTitleFragmentNode: GraphQLTaggedNode = graphql`
	fragment useEmojiTitleFragment_content on Content @catch {
		id
		emojiTitlePublished: properties(key: "emoji-title-published") {
			nodes {
				id
				key
				value
				version {
					number
				}
			}
		}
		emojiTitleDraft: properties(key: "emoji-title-draft") {
			nodes {
				id
				key
				value
				version {
					number
				}
			}
		}
		contentProperties {
			latest {
				defaultTitleEmoji
			}
		}
	}
`;

/**
 * The emoji title from EditorContentUnifiedQuery in the shape of EmojiTitleQuery data. When the
 * fragment is provided, its reader skips EmojiTitleQuery, whose refetch still updates the records
 * the fragment reads. With skip, the fragment is not read, as the query was not run.
 */
export function useEmojiTitleFragment({
	contentId,
	skip = false,
}: {
	contentId: string;
	skip?: boolean;
}): EditorContentFragmentResult<EmojiTitleQuery$data> {
	return useEditorContentFragment<useEmojiTitleFragment_content$key, EmojiTitleQuery$data>(
		'emojiTitleFragment',
		emojiTitleFragmentNode,
		{ contentId, skip, mapData: (value) => ({ content: { nodes: [value] } }) },
	);
}
